import { useCallback, useEffect, useRef, useState } from 'react'
import {
  desserializarSessaoSalva,
  serializarSessaoSalva,
  type SessaoSalva,
} from '../../dominio/tabletop/atendimento'
import type { RetornoUseAtendimentoTabletop } from './useAtendimentoTabletop'
import type { useTabletop } from './useTabletop'

type Tabletop = ReturnType<typeof useTabletop>

export const CHAVE_SESSAO_TABLETOP_LOCAL = 'csv-lab-tabletop-sessao-v1'

/** O que o aviso "Há uma sessão salva" mostra antes de o instrutor decidir. */
export interface SessaoPendente {
  readonly nomeCena: string
  readonly tempoSimuladoSegundos: number
  readonly salvoEm: number
}

function lerSessaoSalva(): SessaoSalva | null {
  if (typeof window === 'undefined') return null
  try {
    const salvo = window.localStorage.getItem(CHAVE_SESSAO_TABLETOP_LOCAL)
    return salvo ? desserializarSessaoSalva(salvo) : null
  } catch {
    return null
  }
}

function apagarSessaoSalva() {
  try {
    window.localStorage.removeItem(CHAVE_SESSAO_TABLETOP_LOCAL)
  } catch {
    // sem armazenamento: nada a apagar
  }
}

/**
 * Sessão de atendimento guardada no navegador, para recarregar a página no
 * meio da aula sem perder nada — mesmo padrão de `useSessaoExtracao`, com uma
 * diferença: a sessão NÃO volta sozinha. Ao abrir, uma sessão salva fica
 * "pendente" e o instrutor escolhe retomar ou descartar; uma sessão velha de
 * outro dia abrindo direto em Atender confundiria.
 *
 * Salva (com atraso) enquanto está em Atender; apaga ao voltar para Montar
 * (a sessão acabou) ou ao descartar. Sem armazenamento (janela privada), o
 * app segue funcionando, só sem esse recurso.
 */
export function usePersistenciaSessao(tabletop: Tabletop, atendimento: RetornoUseAtendimentoTabletop) {
  const { modo, cena, cenaPreparada, restaurarSessao, mesaCompartilhada } = tabletop
  const { sessao, restaurarSessaoAtendimento } = atendimento

  // lida UMA vez, ao abrir: é o que o aviso oferece para retomar
  const [salva, setSalva] = useState<SessaoSalva | null>(() => lerSessaoSalva())

  useEffect(() => {
    if (!mesaCompartilhada && (modo !== 'atender' || !cenaPreparada)) return
    if (mesaCompartilhada && salva) return
    const agendamento = window.setTimeout(() => {
      try {
        window.localStorage.setItem(
          CHAVE_SESSAO_TABLETOP_LOCAL,
          serializarSessaoSalva({ versao: 1, salvoEm: Date.now(), cenaSessao: cena, cenaPreparada: cenaPreparada ?? cena, sessao }),
        )
      } catch {
        // sem persistência; a sessão continua funcionando normalmente
      }
    }, 500)
    return () => window.clearTimeout(agendamento)
  }, [modo, cena, cenaPreparada, sessao, mesaCompartilhada, salva])

  // uma sessão nova em Atender substitui a oferecida no aviso
  useEffect(() => {
    if (modo === 'atender') setSalva(null)
  }, [modo])

  // Atender → Montar: a sessão terminou, não há o que retomar
  const modoAnterior = useRef(modo)
  useEffect(() => {
    if (modoAnterior.current === 'atender' && modo === 'montar') apagarSessaoSalva()
    modoAnterior.current = modo
  }, [modo])

  const retomar = useCallback(() => {
    if (!salva) return
    restaurarSessao(salva.cenaSessao, salva.cenaPreparada)
    restaurarSessaoAtendimento(salva.sessao)
    setSalva(null)
  }, [salva, restaurarSessao, restaurarSessaoAtendimento])

  const descartar = useCallback(() => {
    apagarSessaoSalva()
    setSalva(null)
  }, [])

  const sessaoPendente: SessaoPendente | null = salva
    ? { nomeCena: salva.cenaPreparada.nome, tempoSimuladoSegundos: salva.sessao.tempoSimuladoSegundos, salvoEm: salva.salvoEm }
    : null

  return { sessaoPendente, retomar, descartar }
}

